import { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';

interface AdminUser {
  id: string;
  role: 'admin' | 'super_admin';
  email: string;
}

interface AdminAuthContextType {
  user: User | null;
  adminUser: AdminUser | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType>({
  user: null,
  adminUser: null,
  loading: true,
  signOut: async () => {},
});

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [initializing, setInitializing] = useState(true);

  // Shorter timeout for faster feedback
  const AUTH_INIT_TIMEOUT_MS = 5000;

  // Prevent the admin route guard from getting stuck forever if a Supabase request hangs.
  const withTimeout = async <T,>(promise: PromiseLike<T>, label: string): Promise<T> => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      return await Promise.race([
        promise,
        new Promise<T>((_resolve, reject) => {
          timeoutId = setTimeout(() => {
            reject(new Error(`Admin auth operation timed out (${label}) after ${AUTH_INIT_TIMEOUT_MS}ms`));
          }, AUTH_INIT_TIMEOUT_MS);
        }),
      ]);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  };

  const checkAdminStatus = async (user: User | null) => {
    if (!user) {
      setAdminUser(null);
      return;
    }

    try {
      const { data, error } = await withTimeout(
        supabase
          .from('admin_users')
          .select('id, role, email')
          .eq('id', user.id)
          .limit(1)
          .maybeSingle(),
        'checkAdminStatus'
      );

      if (error) {
        console.error('Admin check error:', error);
        setAdminUser(null);
        return;
      }

      if (data) {
        setAdminUser(data as AdminUser);
        console.log('Admin user loaded:', data);
      } else {
        setAdminUser(null);
      }
    } catch (error) {
      console.error('Admin check error:', error);
      setAdminUser(null);
    }
  };

  useEffect(() => {
    let mounted = true;

    // Get initial session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await withTimeout(supabase.auth.getSession(), 'getSession');
        if (error) throw error;
        
        if (mounted) {
          setUser(session?.user ?? null);
          if (session?.user) {
            await checkAdminStatus(session.user);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (mounted) {
          setUser(null);
          setAdminUser(null);
        }
      } finally {
        if (mounted) {
          setInitializing(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session?.user?.id);
      if (!mounted) return;

      setUser(session?.user ?? null);

      if (event === 'SIGNED_OUT' || !session?.user) {
        setAdminUser(null);
        return;
      }

      await checkAdminStatus(session.user);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setAdminUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    user,
    adminUser,
    loading: initializing,
    signOut,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};
